import React, { useEffect, useState } from 'react';
import styles from '../../assets/styles/Setting.module.scss';
import AccountSettingsLayout from '../../components/Setting/AccountSettingsLayout';
import { useUser } from '../../contexts/UserContext';
import { Theme, useTheme } from '../../contexts/ThemeContext';

const THEME_OPTIONS: Array<{ value: Theme; label: string }> = [
  { value: 'dark', label: 'ダークモード' },
  { value: 'light', label: 'ホワイトモード' },
];

const Profile: React.FC = () => {
  const { user, fetchUser } = useUser();
  const { theme, setTheme } = useTheme();

  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [password, setPassword] = useState<string | null>('********');
  const [confirmPassword, setConfirmPassword] = useState<string | null>('');
  const [isSavingAccount, setIsSavingAccount] = useState(false);

  const resetAccountForm = () => {
    if (!user) return;
    setFirstName(user.first_name || '');
    setLastName(user.last_name || '');
    setEmail(user.email || '');
    setContactPhone(user.tel_num || '');
    setPassword('********');
    setConfirmPassword('');
  };

  useEffect(() => {
    if (!user) return;
    if (!isEditingAccount) {
      resetAccountForm();
    }
  }, [user, isEditingAccount]);

  const toggleAccountEdit = async () => {
    if (!isEditingAccount) {
      setPassword(null);
      setConfirmPassword(null);
      setIsEditingAccount(true);
      return;
    }

    if (!user) {
      alert('ユーザー情報が取得できていません。しばらくして再度お試しください。');
      return;
    }

    const nextFirstName = firstName.trim();
    const nextLastName = lastName.trim();
    const nextContactPhone = contactPhone.trim();
    const hasPasswordInput = (password && password.length > 0) || (confirmPassword && confirmPassword.length > 0);

    if (hasPasswordInput && password !== confirmPassword) {
      alert('パスワードと確認用パスワードが一致しません。');
      return;
    }

    try {
      setIsSavingAccount(true);
      const payload: any = {
        first_name: nextFirstName,
        last_name: nextLastName,
        tel_num: nextContactPhone || null,
      };

      if (hasPasswordInput) {
        payload.password = password;
      }

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/update-me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('更新に失敗しました');
      }

      await fetchUser();
      setIsEditingAccount(false);
      setPassword('********');
      setConfirmPassword('');
      alert('アカウント情報を更新しました');
    } catch (e) {
      console.error(e);
      alert('アカウント情報の更新に失敗しました');
    } finally {
      setIsSavingAccount(false);
    }
  };

  const updateTheme = async (nextTheme: Theme) => {
    if (!user || nextTheme === theme) return;

    const previousTheme = theme;
    setTheme(nextTheme);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/update-me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: user.first_name,
          last_name: user.last_name,
          tel_num: user.tel_num || null,
          theme: nextTheme,
        }),
      });
      if (!res.ok) throw new Error('テーマ設定の保存に失敗しました');
      await fetchUser();
    } catch (error) {
      console.error(error);
      setTheme(previousTheme);
      alert('表示モードの保存に失敗しました');
    }
  };

  return (
    <AccountSettingsLayout>
      <div className={styles.twoColumnGrid}>
        <div className={styles.leftColumn}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>アカウント情報</h3>
              <button
                className={styles.actionButton}
                onClick={toggleAccountEdit}
                disabled={isSavingAccount}
              >
                {isSavingAccount ? '保存中...' : isEditingAccount ? '保存' : '編集'}
              </button>
            </div>

            <div className={styles.formContainer}>
              <div className={styles.formGroup}>
                <label>ユーザー名</label>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  className={styles.inputField}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={!isEditingAccount}
                  placeholder="姓"
                />
                <input
                  type="text"
                  className={styles.inputField}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={!isEditingAccount}
                  placeholder="名"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>メールアドレス</label>
              {isEditingAccount ? (
                <div className={styles.emailDisplay}>
                  <div className={styles.emailText}>{email}</div>
                  <button
                    className={`${styles.miniButton} ${styles.miniButtonSmall}`}
                    onClick={() => alert('メールアドレス変更機能は現在開発中です')}
                  >
                    メールアドレスを変更
                  </button>
                </div>
              ) : (
                <input
                  type="email"
                  className={styles.inputField}
                  value={email}
                  disabled
                />
              )}
            </div>

            <div className={styles.formGroup}>
              <label>担当者電話番号</label>
              <input
                type="tel"
                className={styles.inputField}
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                disabled={!isEditingAccount}
                placeholder="例: 090-1234-5678"
              />
            </div>

            <div className={styles.formGroup}>
              <label>パスワード</label>
              <input
                type="password"
                className={styles.inputField}
                value={password || ''}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!isEditingAccount}
                placeholder={isEditingAccount ? "新しいパスワード" : ""}
              />
            </div>

            {isEditingAccount && (
              <div className={styles.formGroup}>
                <label>パスワード (確認用)</label>
                <input
                  type="password"
                  className={styles.inputField}
                  value={confirmPassword || ''}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="新しいパスワードを再入力"
                />
              </div>
            )}

            <div className={styles.formGroup}>
              <label>権限</label>
              <input
                type="text"
                className={styles.inputField}
                value="一般"
                disabled
              />
            </div>

            {user?.is_admin && (
              <div className={styles.formGroup}>
                <label>表示モード</label>
                <div className={styles.themeRadioGroup}>
                  {THEME_OPTIONS.map((option) => (
                    <label key={option.value} className={styles.themeRadioLabel}>
                      <input
                        type="radio"
                        name="theme"
                        value={option.value}
                        checked={theme === option.value}
                        onChange={() => updateTheme(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </AccountSettingsLayout>
  );
};

export default Profile;
